/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { RecordTable, RecordValue, tableProps } from "./schema";

// Code adopted from the 'sql-template-tag' package
// https://github.com/blakeembrey/sql-template-tag
// MIT license

/**
 * Values supported by SQL engine.
 */
export type Value = unknown;
/**
 * Supported value or SQL instance.
 */
export type RawValue = Value | Sql;

/**
 * A SQL instance can be nested within each other to build SQL strings.
 */
export class Sql {
  readonly values: Value[];
  readonly strings: string[];
  constructor(rawStrings: readonly string[], rawValues: readonly RawValue[]) {
    if (rawStrings.length - 1 !== rawValues.length) {
      if (rawStrings.length === 0) {
        throw new TypeError("Expected at least 1 string");
      }
      throw new TypeError(`Expected ${rawStrings.length} strings to have ${rawStrings.length - 1} values`);
    }
    const valuesLength = rawValues.reduce(
      (len: number, value) => len + (value instanceof Sql ? value.values.length : 1),
      0,
    ) as number;

    this.values = new Array(valuesLength);
    this.strings = new Array(valuesLength + 1);
    this.strings[0] = rawStrings[0]!;
    // Iterate over raw values, strings, and children. The value is always
    // positioned between two strings, e.g. `index + 1`.
    let i = 0,
      pos = 0;

    while (i < rawValues.length) {
      const child = rawValues[i++];
      const rawString = rawStrings[i];
      // Check for nested `sql` queries.
      if (child instanceof Sql) {
        // Append child prefix text to current string.
        this.strings[pos] += child.strings[0]!;
        let childIndex = 0;
        while (childIndex < child.values.length) {
          this.values[pos++] = child.values[childIndex++];
          this.strings[pos] = child.strings[childIndex]!;
        }
        // Append raw string to current string.
        this.strings[pos] += rawString!;
      } else {
        this.values[pos++] = child;
        this.strings[pos] = rawString!;
      }
    }
  }

  get text() {
    let i = 1,
      value = this.strings[0];
    while (i < this.strings.length) value += `$${i}${this.strings[i++]}`;
    return value as string;
  }

  get sql() {
    let i = 1,
      value = this.strings[0];
    while (i < this.strings.length) value += `?${this.strings[i++]}`;
    return value as string;
  }

  inspect() {
    return {
      text: this.text,
      sql: this.sql,
      values: this.values,
    };
  }
}
/**
 * Create a SQL query for a list of values.
 */
export function join(values: readonly RawValue[], separator = ",", prefix = "", suffix = ""): Sql {
  if (values.length === 0) {
    throw new TypeError("Expected `join([])` to be called with an array of multiple elements, but got an empty array");
  }
  return new Sql([prefix, ...Array(values.length - 1).fill(separator), suffix], values);
}
/**
 * Create a SQL query for a list of structured values.
 */
export function bulk(data: ReadonlyArray<ReadonlyArray<RawValue>>, separator = ",", prefix = "", suffix = ""): Sql {
  const length = data.length && data[0]!.length;
  if (length === 0) {
    throw new TypeError(
      "Expected `bulk([][])` to be called with a nested array of multiple elements, but got an empty array",
    );
  }
  const values = data.map((item, index) => {
    if (item.length !== length) {
      throw new TypeError(`Expected \`bulk([${index}][])\` to have a length of ${length}, but got ${item.length}`);
    }
    return new Sql(["(", ...Array(item.length - 1).fill(separator), ")"], item);
  });
  return new Sql([prefix, ...Array(values.length - 1).fill(separator), suffix], values);
}
/**
 * Create raw SQL statement.
 */
export function raw(value: string): Sql {
  return new Sql([value], []);
}
/**
 * Placeholder value for "no text".
 */
export const empty: Sql = raw("");

export function comment(_commentText?: string): Sql {
  return empty;
}

/**
 * Create a SQL object from a template string.
 */
export default function _sql(strings: readonly string[], ...values: readonly RawValue[]): Sql {
  return new Sql(strings, values);
}

export type SqlValue = string | number | null | bigint | Uint8Array | Int8Array | ArrayBuffer;

export type Statement = {
  /** The sql string query */
  text: string;
  /** Array of user provided parameters (possibly unsafe) */
  values?: any[];
};

const sql = _sql as typeof _sql & {
  EMPTY: typeof empty;
  join: typeof join;
  raw: typeof raw;
  bulk: typeof bulk;
  comment: typeof comment;
};

sql.EMPTY = empty;
sql.join = join;
sql.raw = raw;
sql.bulk = bulk;
sql.comment = comment;

export function getNamespacedTableColumnsForSelect<T extends RecordTable>(
  /**
   * An array of the table names in the result. If a table in the
   * result has been renamed, the entry here will be a tuple of
   * [tableName, mappedTableName]
   */
  ...tables: Array<T | { table: T; as: string }>
) {
  return sql.join(
    tables.flatMap((table) =>
      typeof table === "object" ?
        tableProps[table.table].map((prop) => sql.raw(`"${table.as}"."${prop}" as "${table.as}__${prop}"`))
      : tableProps[table].map((prop) => sql.raw(`"${table}"."${prop}" as "${table}__${prop}"`)),
    ),
    ",\n",
  );
}

export type RawResult = Record<string, any>[];
/**
 * An array of tuples. Each tuple represents a row of the database's response.
 * The tuples contain parsed PointerWithRecord values from that row. The
 * parsed PointerWithRecord values within the tuple are in no particular order.
 */
export type ParsedResult<Table extends RecordTable = RecordTable> = ParsedRow<Table>[];

export type ParsedRow<Table extends RecordTable = RecordTable> = ParsedRecord<Table>[];

export type ParsedRecord<T extends RecordTable = RecordTable> = {
  [K in T]: {
    table: K;
    id: string;
    record: RecordValue<K> & { __can_authorization_fail: boolean };
  };
}[T];

export function parseNamespacedDatabaseRows(props: {
  rows: RawResult;
  /**
   * An array of the table names in the result. If a table in the
   * result has been renamed, the entry here will be a tuple of
   * [tableName, mappedTableName]
   */
  tables: Array<RecordTable | { table: RecordTable; as?: string; canAuthorizationFail?: boolean }>;
  decodeRecord: (table: RecordTable, record: Record<string, any>) => RecordValue;
}): ParsedResult {
  const { rows, tables, decodeRecord } = props;

  const normalizedTables = tables.map((table) => {
    const tableName = typeof table === "object" ? table.table : table;

    const mappedTableName = typeof table === "string" ? table : table.as || table.table;

    const canAuthorizationFail = (typeof table === "object" && table.canAuthorizationFail) || false;

    const props = tableProps[tableName];

    if (!props) {
      throw new Error(`mapNamespacedRowsToRecordMap: unknown table name "${tableName}"`);
    }

    return {
      tableName,
      mappedTableName,
      props,
      canAuthorizationFail,
    };
  });

  return rows.map((row) => {
    const parsedRow = [] as unknown as ParsedRow;

    normalizedTables.forEach(({ tableName, mappedTableName, props, canAuthorizationFail }) => {
      const record = {} as any;

      for (const prop of props) {
        record[prop] = row[`${mappedTableName}__${prop}`];
      }

      // If the record was optional and the query supplied `null` values for the
      // record because it was part of a UNION query and the results needed to be
      // consistent, then it's possible for a record to have all `null` values.
      // We should ignore a record like this.
      if (!record.id) return;

      const decodedRecord = decodeRecord(tableName, record);

      const parsedRecord = {
        table: tableName,
        id: decodedRecord.id,
        record: decodedRecord,
      } as ParsedRecord;

      parsedRecord.record.__can_authorization_fail = canAuthorizationFail;

      parsedRow.push(parsedRecord);
    });

    return parsedRow;
  });
}

export function parseDatabaseRows<Table extends RecordTable>(props: {
  rows: RawResult;
  table: RecordTable;
  decodeRecord: (table: RecordTable, row: Record<string, any>) => RecordValue;
}): ParsedResult<Table> {
  const { rows, table, decodeRecord } = props;

  return rows.map((row) => {
    const record = decodeRecord(table, row);

    const parsedRecord = {
      table,
      id: record.id,
      record: record,
    } as ParsedRecord<Table>;

    parsedRecord.record.__can_authorization_fail = false;

    return [parsedRecord];
  });
}

export { sql };
